/**
 * Without-Abtretung patient process overview screen
 *
 * Route: `/webAppOnboarding/insurance-without-abtretung-patient-process-overview`. Explains
 * the TK billing variant where Physiofit submits the participation confirmation without IBAN;
 * the insurer pays the patient, who then forwards the refund to Physiofit. No upfront payment.
 * Forwards to the without-Abtretung-patient checkout.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('without_abtretung_patient')`; `useSaveOnboardingStep(...)`.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { space } from '../../../styles/onboardingUiTokens';
import { ListTimeline, ListTimelineItem } from '../components/content/ListTimeline';
import { SvgIcon } from '../components/icons/SvgIcon';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { H1 } from '../components/typography/HeadingLevels';
import { useInsuranceExplainBillingGuard } from '../hooks/useInsuranceExplainBillingGuard';
import { useSelectedInsurance } from '../hooks/useSelectedInsurance';
import { displayInsuranceShortName } from '../insurance/displayInsuranceName';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;

export function WebOnboardingInsuranceWithoutAbtretungPatientProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_without_abtretung_patient_process_overview');
  const posthog = usePostHog();
  const navigate = useNavigate();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );
  const explain = ctx.meta.withoutAbtretungPatientExplain;
  const insuranceName = ctx.insuranceName ?? '';
  const { insurance } = useSelectedInsurance();
  const insuranceShortName = useMemo(
    () => (insurance ? displayInsuranceShortName(insurance) : ''),
    [insurance],
  );
  const bonusStep = useMemo(() => {
    if (!insurance?.bonus?.hasBonus) {
      return null;
    }
    const bonus = insurance.bonus;
    return {
      title: `Prämie von der ${insuranceShortName || insuranceName}`,
      body: bonus.description?.trim() ?? '',
    };
  }, [insurance, insuranceShortName, insuranceName]);

  useInsuranceExplainBillingGuard('without_abtretung_patient');

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'withoutAbtretungPatient',
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
    if (ctx.forwardPath) {
      navigate(ctx.forwardPath);
    }
  };

  const bodyContent = (() => {
    if (!insuranceName || !explain) {
      return null;
    }

    const { timeline } = explain;

    return (
      <ContentColumn>
        <H1>{explain.headlineTemplate}</H1>
        <ListTimeline>
          <ListTimelineItem
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                💪
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.white}
            title={timeline.step1Title}
          >
            {timeline.step1Body}
          </ListTimelineItem>
          <ListTimelineItem
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                📨
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.white}
            title={timeline.step2Title}
          >
            {timeline.step2BodyTemplate}
          </ListTimelineItem>
          <ListTimelineItem
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                🤝
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.white}
            title={timeline.step3Title}
          >
            {timeline.step3BodyTemplate}
          </ListTimelineItem>
          <ListTimelineItem
            marker={<SvgIcon name="check" size={20} primaryColor={colors.white} />}
            circleBackground={colors.success}
            circleBorder={colors.success}
            numberColor={colors.white}
            title={timeline.step4Title}
          >
            {timeline.step4BodyTemplate}
          </ListTimelineItem>
          {bonusStep ? (
            <ListTimelineItem
              marker={
                <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                  🎁
                </span>
              }
              highlighted
              highlightColor={colors.success}
              title={bonusStep.title}
            >
              {bonusStep.body}
            </ListTimelineItem>
          ) : null}
        </ListTimeline>
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={6}
      onBack={handleBack}
      forwardLabel="Weiter"
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingBody>{bodyContent}</OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
